import { useState, useCallback } from "react";
import { assetPath } from "@/utils/assetPath";
import { Drawer } from "@/components/shared/Drawer";
import { Button } from "@/components/shared/atoms/Button";
import Cropper from "react-easy-crop";
import { Area, Point } from "react-easy-crop";

interface ImageCropperBottomSheetProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspectRatio?: number;
  cropShape?: "rect" | "round";
  title?: string;
}

export const ImageCropperBottomSheet = ({
  open,
  onClose,
  imageUrl,
  onCropComplete,
  aspectRatio = 1,
  cropShape = "round",
  title = "Crop Image",
}: ImageCropperBottomSheetProps) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createCroppedImage = useCallback(
    async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
      const image = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      return new Promise((resolve, reject) => {
        image.onload = () => {
          if (!ctx) {
            reject(new Error("Canvas context not available"));
            return;
          }

          canvas.width = pixelCrop.width;
          canvas.height = pixelCrop.height;

          ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
          );

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Canvas toBlob failed"));
              }
            },
            "image/jpeg",
            0.9
          );
        };

        image.onerror = () => {
          reject(new Error("Image load failed"));
        };

        image.src = imageSrc;
      });
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const croppedBlob = await createCroppedImage(imageUrl, croppedAreaPixels);
      onCropComplete(croppedBlob);
      onClose();
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, imageUrl, onCropComplete, onClose, createCroppedImage]);

  const handleClose = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  }, [onClose]);

  return (
    <Drawer
      triggerContent={null}
      open={open}
      onOpenChange={handleClose}
      contentClassName="z-[999] !mb-0"
    >
      <div className="bg-white rounded-t-2xl max-w-md mx-auto w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 my-4 border-b border-black-6 px-2">
          <button onClick={handleClose}>
            <img
              src={assetPath('icons/arrow-left.svg')}
              alt="arrow-left"
              className="w-6 h-6"
            />
          </button>
          <h2 className="text-18 font-semibold">{title}</h2>
          <div />
        </div>

        {/* Crop Area */}
        <div className="relative h-96 bg-black mx-2 rounded-lg overflow-hidden">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={cropShape}
            onCropChange={setCrop}
            onCropComplete={handleCropComplete}
            onZoomChange={setZoom}
            showGrid={false}
            style={{
              containerStyle: {
                position: "relative",
                width: "100%",
                height: "100%",
                backgroundColor: "black",
              },
            }}
          />
        </div>

        {/* Zoom Control */}
        <div className="px-2 py-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-2 pb-4 flex flex-col gap-2">
          <Button
            onClick={handleSave}
            disabled={isProcessing || !croppedAreaPixels}
            fullWidth
            className="py-3.5 font-medium text-sm"
          >
            {isProcessing ? "Processing..." : "Save"}
          </Button>
          <Button
            onClick={handleClose}
            variant="outline"
            fullWidth
            className="py-3.5 font-medium text-sm"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}; 